import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { DEVICE_UI_FEATURE_TOGGLE_TOKEN } from './device-ui-feature-toggle.config';
import { DeviceUiFeatureId } from './device-ui-feature-toggle.models';

/**
 * Service for checking whether a device-ui feature is enabled or disabled.
 *
 * Reads from the {@link DEVICE_UI_FEATURE_TOGGLE_TOKEN} provided by the host application.
 * Features default to **enabled** when not explicitly disabled.
 *
 * Supports hierarchical feature IDs: disabling a parent prefix (e.g. `'settings.wireless-mics'`)
 * implicitly disables all child features (e.g. `'settings.wireless-mics.rf-spectrum-scanner'`).
 *
 * Internally converts the Observable-based config to a Signal,
 * so all public methods are synchronous and template-friendly.
 */
@Injectable({ providedIn: 'root' })
export class DeviceUiFeatureToggleService {
	private readonly disabledFeatures = toSignal(inject(DEVICE_UI_FEATURE_TOGGLE_TOKEN).disabledFeatures$, {
		initialValue: new Set<DeviceUiFeatureId>()
	});

	/**
	 * Returns `true` when the feature is enabled (i.e., neither it nor any of its
	 * ancestor prefixes are in the disabled set).
	 *
	 * @example
	 * ```typescript
	 * // If 'settings.wireless-mics' is disabled:
	 * service.isFeatureEnabled('settings.wireless-mics');                    // false
	 * service.isFeatureEnabled('settings.wireless-mics.rf-spectrum-scanner'); // false (parent disabled)
	 * service.isFeatureEnabled('settings.general');                          // true
	 * ```
	 */
	public isFeatureEnabled(id: DeviceUiFeatureId): boolean {
		const disabled = this.disabledFeatures();
		if (disabled.has(id)) {
			return false;
		}
		// Walk up the prefix tree to check if any ancestor is disabled
		let dotIndex = id.lastIndexOf('.');
		while (dotIndex > 0) {
			if (disabled.has(id.substring(0, dotIndex) as DeviceUiFeatureId)) {
				return false;
			}
			dotIndex = id.lastIndexOf('.', dotIndex - 1);
		}
		return true;
	}

	/** Returns `true` when the feature has been explicitly disabled (or an ancestor is disabled). */
	public isFeatureDisabled(id: DeviceUiFeatureId): boolean {
		return !this.isFeatureEnabled(id);
	}

	/** Returns the full set of explicitly disabled feature IDs. */
	public getDisabledFeatures(): ReadonlySet<DeviceUiFeatureId> {
		return this.disabledFeatures();
	}
}
